// DealRoomPanel — the Insurance Deal Room composite view.
//
// Opens when the agent clicks a lead. One API call to
// /api/deal-room/lead returns everything the agent needs to walk
// into the next call with a full plan:
//
//   · Deal Score (0-100, with the reasons behind it)
//   · Executive summary of who the lead is
//   · Recommended products (life + health), each with a reason
//   · Missing discovery — what to still find out on the next call
//   · Next actions — call now / schedule / send quote
//   · Client fabric — doctor, prescriptions, personal notes,
//     birthday (PHI-safe: null in Demo Mode by design)
//   · Policies on file
//
// UX principles:
//   · One panel, no tabs. Everything visible or one scroll away.
//   · Score at the top, "why" right underneath it. Scoring you
//     can't explain is scoring you can't trust.
//   · Missing discovery is a checklist, not a wall of text — agent
//     glances at it, remembers, moves on.
//   · Next actions are buttons, not paragraphs.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearerHeaders() {
  try {
    const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function scoreColor(score) {
  if (score >= 80) return { bg: 'bg-emerald-100', fg: 'text-emerald-800', ring: 'ring-emerald-200' };
  if (score >= 60) return { bg: 'bg-lime-100',    fg: 'text-lime-800',    ring: 'ring-lime-200' };
  if (score >= 40) return { bg: 'bg-amber-100',   fg: 'text-amber-800',   ring: 'ring-amber-200' };
  return              { bg: 'bg-slate-100',   fg: 'text-slate-700',   ring: 'ring-slate-200' };
}

function fmt$(cents) {
  if (!Number.isFinite(cents)) return null;
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Score band a Deal Score lands in — matches the buckets in
 * api/deal-room/calibration.js. Kept in sync manually; a real
 * regression is caught by the source-level test.
 */
function scoreBandFor(score) {
  if (score < 30)  return '0-30';
  if (score < 50)  return '30-50';
  if (score < 70)  return '50-70';
  if (score < 90)  return '70-90';
  return '90-100';
}

function DealScoreCard({ score, reasons, calibration }) {
  const c = scoreColor(score);
  const rating = score >= 80 ? 'Excellent Opportunity'
               : score >= 60 ? 'Strong Opportunity'
               : score >= 40 ? 'Warm — needs work'
               : 'Cold — heavy lift ahead';

  // "In your book, the 70-90 band has converted 24% (n=62)" — only
  // renders when we have enough calibration data to be trustworthy.
  let calibHint = null;
  if (calibration && Array.isArray(calibration.score_bands)) {
    const band = scoreBandFor(score);
    const row  = calibration.score_bands.find(b => b.band === band);
    if (row && row.snapshot_count >= 5) {
      calibHint = {
        band,
        rate: Math.round(row.rate * 100),
        n: row.snapshot_count,
      };
    }
  }

  return (
    <div className={`${c.bg} rounded-2xl p-4 border ${c.ring} border-2 border-transparent`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Deal Score</div>
          <div className={`text-4xl font-black ${c.fg} leading-none mt-1`}>{score}<span className="text-lg text-slate-500">/100</span></div>
        </div>
        <div className="text-right">
          <div className={`text-sm font-semibold ${c.fg}`}>{rating}</div>
          {calibHint && (
            <div className="text-[10px] text-slate-600 mt-1">
              Your book: {calibHint.rate}% converted in {calibHint.band} (n={calibHint.n})
            </div>
          )}
        </div>
      </div>
      {reasons?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {reasons.map((r, i) => (
            <li key={i} className="text-[11px] text-slate-700 flex gap-1.5 items-start">
              <span className="text-slate-400">·</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Card({ title, children, empty }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">{title}</div>
      {empty ? <div className="text-xs text-slate-500 italic">{empty}</div> : children}
    </div>
  );
}

/**
 * Insurance Journey stepper — left-to-right stages with the current
 * stage highlighted. Reached stages are filled; future stages are
 * outlined. Layout wraps on mobile.
 */
function JourneyStepper({ journey }) {
  if (!journey?.stages?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-3">Insurance journey</div>
      <div className="flex flex-wrap items-center gap-y-2">
        {journey.stages.map((s, i) => {
          const isReached = s.status === 'reached';
          const isCurrent = s.status === 'current';
          const dotClass = isCurrent
            ? 'bg-emerald-600 ring-4 ring-emerald-100'
            : isReached
              ? 'bg-emerald-500'
              : 'bg-slate-200';
          const labelClass = isCurrent
            ? 'text-emerald-800 font-bold'
            : isReached
              ? 'text-slate-700'
              : 'text-slate-400';
          return (
            <React.Fragment key={s.key}>
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${dotClass}`}></div>
                <div className={`text-[11px] ${labelClass}`}>{s.label}</div>
              </div>
              {i < journey.stages.length - 1 && (
                <div className={`h-px flex-1 mx-2 min-w-4 ${isReached ? 'bg-emerald-300' : 'bg-slate-200'}`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Smart Touch card — when + how does this lead actually respond?
 * Only renders when confidence is medium/high. On low confidence
 * we return null so the panel stays clean while the pattern learns.
 */
function SmartTouchCard({ smartTouch }) {
  if (!smartTouch) return null;
  if (smartTouch.confidence === 'low') return null;
  if (!smartTouch.best_hour_range && !smartTouch.best_channel) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">Best time + channel</div>
      <div className="flex flex-wrap gap-4 mb-2">
        {smartTouch.best_hour_range && (
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Best window</div>
            <div className="text-sm font-bold text-slate-900">{smartTouch.best_hour_range}</div>
          </div>
        )}
        {smartTouch.best_channel && (
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Best channel</div>
            <div className="text-sm font-bold text-slate-900 uppercase">{smartTouch.best_channel}</div>
          </div>
        )}
      </div>
      {smartTouch.reasons?.length > 0 && (
        <ul className="space-y-0.5">
          {smartTouch.reasons.map((r, i) => (
            <li key={i} className="text-[11px] text-slate-600 flex gap-1.5 items-start">
              <span className="text-slate-400">·</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * AI Buying Readiness Timeline — sparkline of the last N Deal Score
 * snapshots for this lead. Hidden if fewer than 3 snapshots exist
 * (a single dot isn't a trend). Rendered inline as SVG so it stays
 * self-contained (no chart lib in the bundle).
 */
function ReadinessSparkline({ timeline }) {
  if (!Array.isArray(timeline) || timeline.length < 3) return null;
  const scores = timeline.map(t => t.score);
  const w = 240, h = 40, pad = 4;
  const maxS = 100, minS = 0;
  const stepX = (w - 2 * pad) / Math.max(1, scores.length - 1);
  const y = (s) => h - pad - ((s - minS) / (maxS - minS)) * (h - 2 * pad);
  const points = scores.map((s, i) => `${pad + i * stepX},${y(s)}`).join(' ');
  const last = scores[scores.length - 1];
  const first = scores[0];
  const delta = last - first;
  const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;
  const deltaColor = delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-slate-500';
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Buying readiness · last {scores.length} touches</div>
        <div className={`text-[11px] font-semibold ${deltaColor}`}>{deltaLabel} vs first</div>
      </div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="w-full h-10">
        <polyline
          fill="none"
          stroke="#10b981"
          strokeWidth="1.5"
          points={points}
        />
        {scores.map((s, i) => (
          <circle key={i} cx={pad + i * stepX} cy={y(s)} r={i === scores.length - 1 ? 3 : 1.5} fill="#10b981" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1 tabular-nums">
        <span>Score {first}</span>
        <span>Now {last}</span>
      </div>
    </div>
  );
}

function ProductFitList({ items, kind }) {
  if (!items?.length) return <div className="text-xs text-slate-500 italic">No {kind} recommendations.</div>;
  return (
    <ul className="space-y-2">
      {items.map((p, i) => (
        <li key={i} className="text-xs">
          <div className="font-semibold text-slate-800">{p.product}</div>
          <div className="text-[11px] text-slate-600">{p.reason}</div>
        </li>
      ))}
    </ul>
  );
}

function MissingDiscoveryList({ items }) {
  if (!items?.length) return <div className="text-xs text-emerald-700">All key fields collected.</div>;
  return (
    <div className="grid grid-cols-2 gap-y-1 gap-x-3">
      {items.map(f => (
        <div key={f.key} className="text-[11px] text-slate-600 flex gap-1.5 items-center">
          <span className="w-3 h-3 rounded border border-slate-300 inline-block flex-shrink-0"/>
          <span>{f.label}</span>
        </div>
      ))}
    </div>
  );
}

function NextActionsList({ actions }) {
  if (!actions?.length) return <div className="text-xs text-slate-500 italic">Nothing urgent right now.</div>;
  return (
    <ul className="space-y-1.5">
      {actions.map((a, i) => (
        <li key={i} className="text-xs text-slate-800 flex items-start gap-2">
          <span className="text-slate-400 flex-shrink-0">→</span>
          <span>{a.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ClientFabricRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-[11px] text-slate-700 flex gap-1.5">
      <span className="text-slate-500 min-w-[80px]">{label}:</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function PoliciesTable({ life = [], health = [] }) {
  if (!life.length && !health.length) return <div className="text-xs text-slate-500 italic">No policies on file.</div>;
  return (
    <div className="space-y-2">
      {life.map((p, i) => (
        <div key={`l${i}`} className="text-[11px] text-slate-700 flex justify-between gap-2">
          <span className="font-semibold">{p.carrier} {p.product_type || 'Life'} #{p.policy_number}</span>
          <span className="text-slate-500">{p.status}{p.premium_amount_cents ? ` · ${fmt$(p.premium_amount_cents)}/${p.premium_mode || ''}` : ''}</span>
        </div>
      ))}
      {health.map((p, i) => (
        <div key={`h${i}`} className="text-[11px] text-slate-700 flex justify-between gap-2">
          <span className="font-semibold">{p.carrier} {p.plan_name || 'Health'}</span>
          <span className="text-slate-500">{p.status}{p.monthly_premium ? ` · $${p.monthly_premium}/mo` : ''}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Main panel. Props:
 *   leadId  — required; the API resolves lead from user_state.leads
 *   onClose — optional; renders an X close button when provided
 */
/**
 * Relationship Momentum badge — the "star" feature from Harrison's
 * relationship-trust spec. Shows direction + score + one-line
 * evidence-anchored explanation of WHY momentum is where it is.
 * Matches the "explains exactly why" behavior the spec calls out.
 */
function MomentumBadge({ momentum }) {
  const map = {
    rising:    { bg: 'bg-emerald-50', border: 'border-emerald-200', fg: 'text-emerald-800', arrow: '📈', label: 'Rising' },
    steady:    { bg: 'bg-slate-50',   border: 'border-slate-200',   fg: 'text-slate-700',   arrow: '→',  label: 'Steady' },
    declining: { bg: 'bg-amber-50',   border: 'border-amber-200',   fg: 'text-amber-800',   arrow: '📉', label: 'Declining' },
    dormant:   { bg: 'bg-red-50',     border: 'border-red-200',     fg: 'text-red-800',     arrow: '💤', label: 'Dormant' },
  };
  const s = map[momentum.direction] || map.steady;
  return (
    <div className={`${s.bg} ${s.border} border rounded-xl px-3 py-2.5 flex items-start gap-3`}>
      <div className="text-xl leading-none flex-shrink-0" aria-hidden="true">{s.arrow}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Relationship momentum</div>
          <div className={`text-sm font-bold ${s.fg}`}>{s.label}</div>
          {typeof momentum.score === 'number' && (
            <div className="text-[10px] text-slate-500 tabular-nums ml-auto">{momentum.score >= 0 ? '+' : ''}{momentum.score}</div>
          )}
        </div>
        <div className="text-[11px] text-slate-700 mt-0.5 leading-relaxed">{momentum.explanation}</div>
      </div>
    </div>
  );
}

function SignalRow({ item, colorClass = 'text-emerald-800' }) {
  return (
    <li className="text-xs">
      <div className={`font-semibold ${colorClass}`}>{item.label}</div>
      {item.evidence && (
        <div className="text-[11px] text-slate-600 italic mt-0.5">"{item.evidence}"</div>
      )}
    </li>
  );
}

export default function DealRoomPanel({ leadId, onClose = null }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  const [data, setData]       = React.useState(null);
  // LLM synthesis lives in a separate state so the panel renders the
  // fast bundle immediately and enriches when the synthesis lands.
  const [synth, setSynth]     = React.useState(null);
  const [synthLoading, setSynthLoading] = React.useState(false);
  const [synthError, setSynthError]     = React.useState(null);
  // Calibration (Phase 3) — per-owner score-band + signal correlation
  // data. Owner-scoped, not lead-scoped: the same calibration serves
  // every Deal Room open. Cached in module memory across mounts via
  // the closure below so switching leads doesn't re-fetch on every
  // click. `null` while in-flight or on error → the panel just
  // suppresses the "your book" hint.
  const [calibration, setCalibration] = React.useState(null);

  const loadBundle = React.useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const res = await fetch('/api/deal-room/lead', {
        method: 'POST', headers,
        body: JSON.stringify({ lead_id: leadId }),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) setError(body.error || `status_${res.status}`);
      else setData(body);
    } catch (err) {
      setError(err.message || 'network_error');
    } finally { setLoading(false); }
  }, [leadId]);

  const loadSynth = React.useCallback(async () => {
    if (!leadId) return;
    setSynthLoading(true);
    setSynthError(null);
    setSynth(null);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const res = await fetch('/api/deal-room/synthesize', {
        method: 'POST', headers,
        body: JSON.stringify({ lead_id: leadId }),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) setSynthError(body.error || `status_${res.status}`);
      else setSynth(body.synth);
    } catch (err) {
      setSynthError(err.message || 'network_error');
    } finally { setSynthLoading(false); }
  }, [leadId]);

  // Calibration is owner-scoped, so we fetch it once per mount and
  // re-use across every lead the operator opens without a page
  // reload. Non-blocking; errors are logged and the "your book"
  // hint just doesn't render.
  const loadCalibration = React.useCallback(async () => {
    try {
      const headers = await bearerHeaders();
      const res = await fetch('/api/deal-room/calibration?window_days=90', { headers });
      const body = await res.json();
      if (res.ok && body.ok) setCalibration(body);
    } catch { /* non-fatal — hint just won't render */ }
  }, []);

  // Fire all three in parallel. Fast bundle renders the moment it
  // lands; synthesis + calibration layer in as they arrive.
  React.useEffect(() => {
    loadBundle();
    loadSynth();
    loadCalibration();
  }, [loadBundle, loadSynth, loadCalibration]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading deal room…</div>;
  if (error)   return <div className="p-6 text-sm text-red-700">Failed to load: {error}</div>;
  if (!data)   return null;

  return (
    <div className="max-w-none mx-auto p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Deal Room</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">
            {data.lead?.name || 'Unnamed lead'}
            {data.lead?.state ? <span className="ml-2 text-slate-500 text-sm font-normal">· {data.lead.state}</span> : null}
          </h2>
          {data.lead?.source && <div className="text-[11px] text-slate-500">Source: {data.lead.source}</div>}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg">×</button>
        )}
      </div>

      {/* Insurance Journey stepper — where does the lead sit in the
          funnel right now? Renders first so the agent orients
          instantly ("this is a New Lead" vs "this is a Renewal"). */}
      {data.journey && <JourneyStepper journey={data.journey} />}

      <DealScoreCard score={data.deal_score} reasons={data.deal_score_reasons} calibration={calibration}/>

      {/* AI Buying Readiness sparkline — trend of Deal Score over the
          last N snapshots. Hidden until we have ≥3 snapshots so a
          single dot doesn't masquerade as a trend. */}
      {data.readiness_timeline && <ReadinessSparkline timeline={data.readiness_timeline} />}

      {/* Smart Touch Recommendations — best hour window + best channel
          based on the client's own reply pattern. Hidden on low
          confidence so we don't nudge the agent with a spurious
          pattern from 2 messages. */}
      {data.smart_touch && <SmartTouchCard smartTouch={data.smart_touch} />}

      {/* Relationship Momentum — rising / steady / declining / dormant
          with a one-line evidence-anchored explanation. Renders any
          time we have a momentum object (fast bundle always includes
          one, even if the direction is 'dormant' with a helpful nudge). */}
      {data.momentum && <MomentumBadge momentum={data.momentum} />}

      {/* LLM narrative overlay — appears when the synthesize call
          returns. Shows a spinner while it's inflight, an error
          nudge if it failed, and the executive summary paragraph +
          per-product probability + suggested opening when it lands. */}
      <Card title="Executive summary">
        {synthLoading && !synth && (
          <div className="text-xs text-slate-500 italic">AI is studying the file…</div>
        )}
        {synthError && !synth && (
          <div className="text-xs text-amber-800">
            Summary unavailable ({synthError}). The pure-function bundle above still shows Deal Score, signals, and objections.
          </div>
        )}
        {synth && (
          <div className="space-y-3">
            <p className="text-xs text-slate-800 leading-relaxed">{synth.executive_summary}</p>
            {(synth.probability?.life != null || synth.probability?.health != null) && (
              <div className="flex gap-4 text-[11px]">
                {synth.probability?.life != null && (
                  <div>
                    <div className="text-slate-500 uppercase text-[9px] tracking-wider">Life · estimated</div>
                    <div className="font-bold text-slate-900">{synth.probability.life}%</div>
                  </div>
                )}
                {synth.probability?.health != null && (
                  <div>
                    <div className="text-slate-500 uppercase text-[9px] tracking-wider">Health · estimated</div>
                    <div className="font-bold text-slate-900">{synth.probability.health}%</div>
                  </div>
                )}
              </div>
            )}
            {synth.suggested_opening && (
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Suggested opening</div>
                <div className="text-xs text-slate-800 italic bg-slate-50 rounded p-2">"{synth.suggested_opening}"</div>
              </div>
            )}
            {synth.closing_strategy && (
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Closing strategy</div>
                <div className="text-xs text-slate-700 leading-relaxed">{synth.closing_strategy}</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title={`Buying signals (${data.buying_signals?.length || 0})`} empty={!data.buying_signals?.length ? 'No signals detected yet.' : null}>
          <ul className="space-y-2">
            {(data.buying_signals || []).map((s, i) => <SignalRow key={i} item={s} colorClass="text-emerald-800"/>)}
          </ul>
        </Card>
        <Card title={`Objections (${data.objections?.length || 0})`} empty={!data.objections?.length ? 'No objections raised yet.' : null}>
          <ul className="space-y-2">
            {(data.objections || []).map((o, i) => <SignalRow key={i} item={o} colorClass="text-amber-800"/>)}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="Recommended · Life">
          <ProductFitList items={data.product_fit?.life} kind="life"/>
        </Card>
        <Card title="Recommended · Health">
          <ProductFitList items={data.product_fit?.health} kind="health"/>
        </Card>
      </div>

      <Card title={`Missing discovery (${data.niche_focus})`}>
        <MissingDiscoveryList items={data.missing_discovery}/>
      </Card>

      <Card title="Next actions">
        <NextActionsList actions={data.next_actions}/>
      </Card>

      {/* Client Happiness Score — spec #19. Existing clients only.
          Renders alongside the Referral Predictor when eligible. */}
      {data.happiness?.eligible && (
        <Card title="Client happiness">
          <div className="flex items-baseline gap-3 mb-2">
            <div className="text-3xl font-black text-slate-900 tabular-nums">
              {data.happiness.score}<span className="text-base text-slate-500 font-normal">/100</span>
            </div>
            <div className={`text-[10px] font-bold uppercase tracking-wider ${
              data.happiness.trend === 'happy'   ? 'text-emerald-700' :
              data.happiness.trend === 'at_risk' ? 'text-red-700' :
                                                    'text-slate-500'
            }`}>
              {data.happiness.trend === 'happy'   ? 'Happy · leave them alone or ask for referral' :
               data.happiness.trend === 'at_risk' ? 'At risk · reach out before they churn' :
                                                     'Neutral · steady state'}
            </div>
          </div>
          {data.happiness.reasons?.length > 0 && (
            <ul className="space-y-1">
              {data.happiness.reasons.map((r, i) => (
                <li key={i} className="text-[11px] text-slate-700 flex gap-1.5 items-start">
                  <span className="text-slate-400">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Family Protection Calculator — spec #11. Only renders on
          life-niche Deal Rooms; the panel skips it for health-first
          leads where the calculator would be irrelevant. */}
      {data.family_protection && (
        <Card title="Family Protection · discussion tool">
          <div className="mb-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Estimated coverage gap</div>
            <div className="text-3xl font-black text-slate-900 tabular-nums">
              ${(data.family_protection.gap_cents / 100 / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}k
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              Total need ${(data.family_protection.total_need_cents / 100 / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}k · Existing coverage ${(data.family_protection.existing_cents / 100 / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}k
            </div>
          </div>
          {data.family_protection.breakdown?.length > 0 && (
            <div className="space-y-1.5">
              {data.family_protection.breakdown.map((b, i) => (
                <div key={i} className="text-[11px]">
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-slate-800">{b.label}</span>
                    <span className="text-slate-700 tabular-nums">
                      ${(b.cents / 100 / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}k
                    </span>
                  </div>
                  <div className="text-slate-500 italic text-[10px]">{b.assumption}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Referral Predictor — spec #15. Only renders for existing
          clients (eligible=true means at least one active policy on
          file AND not opted out AND not do-not-contact tagged). For
          pre-sale prospects the whole card is hidden — asking a
          lead who hasn't bought yet to refer someone is nonsense. */}
      {data.referral_prediction?.eligible && (
        <Card title="Referral opportunity">
          <div className="flex items-baseline gap-3 mb-2">
            <div className="text-3xl font-black text-slate-900 tabular-nums">
              {data.referral_prediction.probability}<span className="text-base text-slate-500 font-normal">%</span>
            </div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Estimated referral probability</div>
          </div>
          {data.referral_prediction.reasons?.length > 0 && (
            <ul className="space-y-1">
              {data.referral_prediction.reasons.map((r, i) => (
                <li key={i} className="text-[11px] text-slate-700 flex gap-1.5 items-start">
                  <span className="text-slate-400">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Personal follow-up reminders — the "ask about her
          granddaughter's graduation" surface. Renders when the
          client fabric OR profile has anything useful; hidden
          otherwise so we don't fill space with a placeholder. */}
      {Array.isArray(data.personal_reminders) && data.personal_reminders.length > 0 && (
        <Card title="Ask about — make it human">
          <ul className="space-y-1.5">
            {data.personal_reminders.map((r, i) => (
              <li key={i} className="text-xs text-slate-800 flex gap-2 items-start">
                <span className="text-slate-400 flex-shrink-0">·</span>
                <span>{r.topic}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Calibrated signal correlations from THIS agent's book. Only
          renders when enough data has accumulated (n≥5 per signal).
          Cross-references the signals that fired on THIS lead so
          the agent sees which of the current signals actually
          predict a policy in their historical data. */}
      {calibration && (calibration.signal_correlations?.length > 0 || calibration.objection_correlations?.length > 0) && (
        <Card title={`Calibrated to your book (last ${calibration.window_days} days · n=${calibration.total_snapshots})`}>
          <div className="space-y-3">
            {calibration.signal_correlations?.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider mb-1">Top signals by conversion lift</div>
                <ul className="space-y-1">
                  {calibration.signal_correlations.slice(0, 5).map((c, i) => (
                    <li key={i} className="text-[11px] text-slate-700 flex justify-between gap-2">
                      <span className="font-semibold">{c.label}</span>
                      <span className="text-slate-500 tabular-nums">
                        {Math.round(c.rate * 100)}% (n={c.snapshot_count}) · {c.lift_vs_baseline.toFixed(1)}× baseline
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {calibration.objection_correlations?.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Objections most correlated with conversion in your book</div>
                <ul className="space-y-1">
                  {calibration.objection_correlations.slice(0, 3).map((c, i) => (
                    <li key={i} className="text-[11px] text-slate-700 flex justify-between gap-2">
                      <span className="font-semibold">{c.label}</span>
                      <span className="text-slate-500 tabular-nums">
                        {Math.round(c.rate * 100)}% (n={c.snapshot_count}) · {c.lift_vs_baseline.toFixed(1)}× baseline
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {(data.client || data.medical) && (
        <Card title="Client fabric · what to remember">
          <div className="space-y-1">
            <ClientFabricRow label="Client"       value={data.client?.name} />
            <ClientFabricRow label="DOB"          value={data.client?.dob} />
            <ClientFabricRow label="Doctor"       value={data.medical?.primary_doctor} />
            <ClientFabricRow label="Pharmacy"     value={data.medical?.preferred_pharmacy} />
            <ClientFabricRow label="Best time"    value={data.medical?.best_time_to_call} />
            {Array.isArray(data.medical?.current_prescriptions) && data.medical.current_prescriptions.length > 0 && (
              <ClientFabricRow
                label="Rx"
                value={data.medical.current_prescriptions.slice(0, 5).map(r => `${r?.name || '?'}${r?.dosage ? ` ${r.dosage}` : ''}`).join(', ')}
              />
            )}
            {data.medical?.personal_notes && (
              <div className="text-[11px] text-slate-700 mt-1 italic">
                Personal: {String(data.medical.personal_notes).slice(0, 240)}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card title="Policies on file">
        <PoliciesTable life={data.policies?.life} health={data.policies?.health}/>
      </Card>

      {data.profile?.raw_summary && (
        <Card title="Prior summary">
          <div className="text-xs text-slate-700 leading-relaxed">{data.profile.raw_summary.slice(0, 800)}</div>
        </Card>
      )}
    </div>
  );
}
